/**
 * Responsibility: describe the baseline biome — how tall its terrain is at any coordinate
 * and what colours it uses.
 *
 * This is the ground between everything else. Open, gently rolling, and deliberately
 * uninteresting: it is what gives arriving at a mountain range or a city its contrast, and
 * it is where the eye rests during a long flight.
 *
 * ## The one biome without a suitability field
 *
 * Every other biome earns its place with a noise field. Baseline declares a flat
 * `constantSuitability` instead, which makes it the default: wherever nothing else rises
 * above that level, baseline fills in. See `BASELINE.SUITABILITY` in `config.js` for why
 * that matters.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm } from '../noise.js';
import { createAirfieldClearance, createAirfields } from '../airfields.js';
import { BASELINE, PALETTE } from '../../config.js';

export const baseline = {
  id: 'baseline',
  label: 'Open ground',

  palette: PALETTE.baseline,

  /**
   * Constant suitability, in the same 0..1 space as every other biome's field.
   *
   * The presence of this property is what marks a biome as the default. `biomeField.js`
   * gives it no noise field at all.
   */
  constantSuitability: BASELINE.SUITABILITY,

  /**
   * Build the elevation function for this biome.
   *
   * Plain fBm rather than the ridged variant the mountains use. Ridged noise exists to
   * create crests; this biome wants the opposite — the rounded, blobby look that makes
   * plain fBm wrong for mountains is exactly right for open ground.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    const field = createFbm(seed, 'elevation:baseline', {
      octaves: BASELINE.OCTAVES,
      baseFrequency: 1 / BASELINE.WAVELENGTH,
      persistence: BASELINE.PERSISTENCE,
      lacunarity: BASELINE.LACUNARITY,
    });

    return function sampleBaselineElevation(x, z) {
      /*
        fBm spans [-1, 1]; remapped to [0, 1] so the biome never contributes a negative
        elevation. Blended elevation is a weighted sum, and a negative contribution would
        pull neighbouring terrain down through zero rather than simply flattening it.
      */
      const unit = (field(x, z) + 1) / 2;
      return unit * BASELINE.MAX_ELEVATION;
    };
  },

  /** Highest elevation this biome can produce, in world units. */
  maxElevation: BASELINE.MAX_ELEVATION,

  /**
   * Build the structure placement function: airfields.
   *
   * **The only host, deliberately.** An airfield needs *level* ground rather than a new kind of ground, so it is an
   * installation rather than a biome. Baseline hosts it because it is the flattest ground in the world and a runway is
   * the most demanding thing here to site, because it had no structures of its own so a field is the only built thing
   * for miles, and because two biomes placing sites on independent grids could put two airfields on top of each other
   * across a transition.
   *
   * ## The industrial sites used to be here too, and moving them out simplified this
   *
   * They were briefly a second installation on this host, which needed the two clearances composed by hand — a biome is
   * never shown its own reservation, so neither would have seen the other. That went away when industry became a biome
   * of its own: a region cannot collide with an installation on a different biome, because the biome field never puts
   * both at full weight in the same place, and the cross-biome reservation handles the border.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    return createAirfields(seed, 'baseline');
  },

  /**
   * Publish each field so other biomes keep off it.
   *
   * Trees, rock formations and farm structures all consult this. A conifer growing through a runway is the
   * tree-in-a-building bug on the one surface in the world that is unmistakably meant to be clear.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation(seed) {
    return createAirfieldClearance(seed, 'baseline');
  },
};
